import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Decimation,
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import "./../styles/Environment.style.css";
import resourceTypes, {
  AwsMetric,
  Resource,
  ResourcesData,
} from "../models/ResourcesTypes";
import React, { useEffect, useState } from "react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Decimation,
);

export const chartOptions: ChartOptions<"line"> = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
      },
    },
    x: {
      display: false,
    },
  },
};

interface EnvironmentProps {
  title: string;
  resources: ResourcesData;
  filter: string;
  gridView: string;
  statusType: string;
  resourceType: string;
  anyFilter: boolean;
  zeroRun: boolean;
}

const Environment = (props: EnvironmentProps) => {
  const {
    title,
    resources,
    filter,
    gridView,
    statusType,
    resourceType,
    anyFilter,
    zeroRun,
  } = props;

  const [envResources, setEnvResources] = useState<Resource[]>([]);

  useEffect(() => {
    setEnvResources(resources[title] as Resource[]);
  }, []);

  function downSampleData(data: AwsMetric[], maxPoints = 25) {
    if (data.length <= maxPoints) return data;
    const step = Math.ceil(data.length / maxPoints);
    const downSampled = [];
    for (let i = 0; i < data.length; i += step) {
      const chunk = data.slice(i, i + step);
      const summedValue = chunk.reduce((acc, item) => acc + (item.Sum || 0), 0);
      downSampled.push({
        ...chunk[0],
        Sum: summedValue,
      });
    }
    return downSampled;
  }

  function isResourceInError(resource: Resource) {
    if (resource.subtype === "athena" || resource.data.length === 0) {
      return false;
    }
    const downSampledData = downSampleData(resource.data as AwsMetric[]);
    return downSampledData[downSampledData.length - 1].Sum > 0;
  }

  function successRate(resource: Resource) {
    if (resource.invocations === 0) return 100;
    return (
      ((resource.invocations - resource.errors) / resource.invocations) *
      100
    ).toFixed(2);
  }

  return (
    <div className="environment-container">
      <h2>{title}</h2>
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${gridView}, 1fr)` }}
      >
        {envResources &&
          envResources.length !== 0 &&
          envResources
            .filter((resource) => resource.subtype === "athena")
            .map((resource: Resource, index: number) => {
              if (!resource.name.toLowerCase().includes(filter.toLowerCase()))
                return null;
              if (
                !resource.subtype
                  .toLowerCase()
                  .includes(resourceType.toLowerCase())
              )
                return null;
              if (statusType === "error") return null;
              if (anyFilter) return null;
              if (zeroRun) return null;
              const tableData = resource.data as string[][];
              const headers = tableData.length > 0 ? tableData[0] : [];
              const rows = tableData.length > 1 ? tableData.slice(1) : [];
              return (
                <div key={index} className="athena-resource">
                  <table className="athena-table">
                    <thead>
                      <tr>
                        {headers.map((header, hIndex) => (
                          <th key={hIndex}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, rIndex) => (
                        <tr key={rIndex}>
                          {row.map((cell, cIndex) => (
                            <td
                              key={cIndex}
                              style={{
                                color: cell.includes("NOK")
                                  ? "var(--error-color)"
                                  : cell.includes("OK")
                                    ? "var(--success-color)"
                                    : "var(--font-color)",
                              }}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
        {envResources &&
          envResources.length !== 0 &&
          envResources
            .filter((resource) => resource.subtype !== "athena")
            .map(
              (resource: Resource, index: number) =>
                resource.name.toLowerCase().includes(filter.toLowerCase()) &&
                resource.subtype
                  .toLowerCase()
                  .includes(resourceType.toLowerCase()) &&
                (statusType === "" ||
                  (statusType === "error" && isResourceInError(resource)) ||
                  (statusType === "success" && !isResourceInError(resource))) &&
                ((anyFilter &&
                  successRate(resource) !== "100.00" &&
                  resource.invocations !== 0) ||
                  !anyFilter) &&
                ((zeroRun && resource.invocations === 0) || !zeroRun) && (
                  <div
                    className="resource"
                    key={index}
                    style={{
                      background: isResourceInError(resource)
                        ? "var(--error-color-light)"
                        : "",
                    }}
                  >
                    <div className="metrics">
                      <span
                        className="errors"
                        style={{
                          color: isResourceInError(resource)
                            ? "var(--error-color)"
                            : "var(--text-color)",
                        }}
                      >
                        {resource.errors}
                      </span>
                      <span className="invocations">
                        {resource.invocations}
                      </span>

                      {resource.invocations > 0 && (
                        <span
                          className="rate"
                          style={{
                            fontSize: "0.8em",
                            color: isResourceInError(resource)
                              ? "var(--error-color)"
                              : "grey",
                          }}
                        >
                          {successRate(resource)}%
                        </span>
                      )}
                      <div
                        className="status"
                        style={{
                          backgroundColor: isResourceInError(resource)
                            ? "var(--error-color)"
                            : "",
                        }}
                      ></div>
                    </div>
                    <Line
                      options={chartOptions}
                      data={{
                        labels: downSampleData(
                          resource.data as AwsMetric[],
                        ).map((d: AwsMetric) => d.Timestamp),
                        datasets: [
                          {
                            label: "errors",
                            data: downSampleData(
                              resource.data as AwsMetric[],
                            ).map((d: AwsMetric) => d.Sum),
                            borderColor: "#006ce0",
                            backgroundColor: "#006ce033",
                          },
                        ],
                      }}
                    ></Line>
                    <span className="name">{resource.name}</span>
                    <span
                      className="label"
                      style={{
                        background:
                          resourceTypes.find(
                            (rt) => rt.name === resource.subtype,
                          )?.color || "var(--text-color)",
                      }}
                    >
                      {resource.subtype}
                    </span>
                  </div>
                ),
            )}
      </div>
    </div>
  );
};

export default Environment;
